export type NotificationSoundName = "default" | "chime" | "pop" | "ding" | "marimba";

let sharedContext: AudioContext | null = null;
function getContext(): AudioContext | null {
  if (sharedContext) return sharedContext;
  try {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    sharedContext = new Ctor();
    return sharedContext;
  } catch {
    return null;
  }
}

interface Note {
  frequency: number;
  startOffset: number;
  duration: number;
  gain: number;
}

const SOUND_NOTES: Record<NotificationSoundName, Note[]> = {
  default: [
    { frequency: 1046.5, startOffset: 0, duration: 0.18, gain: 0.25 },
    { frequency: 1318.5, startOffset: 0.09, duration: 0.22, gain: 0.22 }
  ],
  chime: [
    { frequency: 523.3, startOffset: 0, duration: 0.15, gain: 0.2 },
    { frequency: 659.3, startOffset: 0.08, duration: 0.15, gain: 0.2 },
    { frequency: 784.0, startOffset: 0.16, duration: 0.25, gain: 0.22 }
  ],
  pop: [{ frequency: 220, startOffset: 0, duration: 0.08, gain: 0.3 }],
  ding: [{ frequency: 1760, startOffset: 0, duration: 0.5, gain: 0.18 }],
  marimba: [
    { frequency: 587.3, startOffset: 0, duration: 0.2, gain: 0.2 },
    { frequency: 440, startOffset: 0.1, duration: 0.28, gain: 0.18 }
  ]
};

export function playNotificationSound(name: NotificationSoundName = "default") {
  const ctx = getContext();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});

  try {
    const notes = SOUND_NOTES[name] ?? SOUND_NOTES.default;
    const now = ctx.currentTime;
    for (const note of notes) {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = note.frequency;
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      const start = now + note.startOffset;
      const end = start + note.duration;
      gainNode.gain.setValueAtTime(0, start);
      gainNode.gain.linearRampToValueAtTime(note.gain, start + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, end);

      oscillator.start(start);
      oscillator.stop(end + 0.02);
    }
  } catch {
    // Never let a synthesis quirk on some browser/device surface as an error
    // to the person - the sound simply doesn't play that once.
  }
}
